import { supabase } from "@/lib/supabase";
import { plainError } from "@/lib/plain-error";
import { linkableObjectives, type LinkableObjective } from "@/lib/priority-link";
import type { BoardChange, BoardRepo } from "./repo";
import { mergeStaff, missingOpenColumn, type DirectoryPerson } from "./open-board";
import type {
  AssigneeLink, BoardRow, BoardSnapshot, Card, ColumnRow, Label, LabelColor, LabelLink, Person,
} from "./types";

const BOARD_COLS = "id,name,description,owner_employee_id,team_id,requires_priority_link,key_prefix,standup_days,standup_time,standup_tz,open_to_all_staff";
// Before sql/2027-02-18_board_open_to_all_staff.sql. Naming the new column
// fails the whole select, so the board still opens and is treated as closed.
const BOARD_COLS_BEFORE_OPEN = "id,name,description,owner_employee_id,team_id,requires_priority_link,key_prefix,standup_days,standup_time,standup_tz";
const COLUMN_COLS = "id,board_id,name,position,is_done,kind";
const CARD_COLS = "id,board_id,column_id,number,title,description,position,priority,due_date,pe_objective_id,created_by,created_by_agent_id,completed_at,moved_at,archived_at,archived_by,archive_reason,created_at,updated_at";
// A separate literal, not a .replace() of the line above: supabase-js types a
// non-literal select string as an error, and this is the list from before
// sql/2027-02-17_complete_column_archive.sql adds the two archive columns.
const CARD_COLS_BEFORE_ARCHIVE = "id,board_id,column_id,number,title,description,position,priority,due_date,pe_objective_id,created_by,created_by_agent_id,completed_at,moved_at,created_at,updated_at";
const LABEL_COLS = "id,board_id,name,color,position";
const PERSON_COLS = "id,first_name,last_name,preferred_name,email,role,employee_kind";

function fail(error: unknown, action: string): never {
  throw new Error(plainError(error, action));
}

function missingArchiveColumn(error: { message?: string } | null): boolean {
  return !!error && /archived_at|archived_by|archive_reason/i.test(error.message ?? "");
}

function withArchive<T extends { archived_at?: string | null; archived_by?: string | null; archive_reason?: string | null }>(row: T): T {
  return { archived_at: null, archived_by: null, archive_reason: null, ...row };
}

export class SupabaseBoardRepo implements BoardRepo {
  async load(boardId: string): Promise<BoardSnapshot> {
    const board = await this.loadBoard(boardId);

    const [cols, cards, labels, members, staff, directory, team] = await Promise.all([
      supabase.from("hq_task_columns").select(COLUMN_COLS).eq("board_id", boardId).order("position"),
      this.loadCards(boardId),
      supabase.from("hq_task_labels").select(LABEL_COLS).eq("board_id", boardId).order("position"),
      supabase.from("hq_board_members").select("employee_id").eq("board_id", boardId),
      // RLS on hq_employees is self + admin. The directory is the roster
      // every signed-in employee can read, so a non-admin can still assign
      // coworkers on a board that is open to all staff.
      supabase.from("hq_employees").select(PERSON_COLS).eq("status", "active")
        .in("employee_kind", ["human", "ai"]).order("first_name"),
      supabase.from("hq_directory").select("id,first_name,last_name,email,role").order("first_name"),
      board.team_id
        ? supabase.from("hq_team_members").select("employee_id").eq("team_id", board.team_id)
        : Promise.resolve({ data: [] as { employee_id: string }[], error: null }),
    ]);
    for (const [r, what] of [[cols, "load the columns"], [labels, "load the labels"],
      [members, "load the members"], [staff, "load the team"], [team, "load the team"]] as const) {
      if (r.error) fail(r.error, what);
    }
    // A missing directory must not blank the board. Admins still have the
    // employee list; everyone else keeps whoever that list returned.
    const people = mergeStaff(
      (directory.error ? [] : (directory.data ?? []) as DirectoryPerson[]),
      (staff.data ?? []) as Person[],
    );

    // Only this board's cards: scoped by task id, not the whole company's
    // assignee table.
    const cardIds = cards.map(c => c.id);
    const [assignees, labelLinks] = cardIds.length
      ? await Promise.all([
          supabase.from("hq_task_assignees").select("task_id,employee_id").in("task_id", cardIds),
          supabase.from("hq_task_label_links").select("task_id,label_id").in("task_id", cardIds),
        ])
      : [{ data: [], error: null }, { data: [], error: null }];
    if (assignees.error) fail(assignees.error, "load who is on each card");
    if (labelLinks.error) fail(labelLinks.error, "load card labels");

    const memberIds = new Set<string>((members.data ?? []).map(m => m.employee_id));
    for (const m of team.data ?? []) memberIds.add(m.employee_id);
    if (board.owner_employee_id) memberIds.add(board.owner_employee_id);

    return {
      board: board as BoardRow,
      columns: (cols.data ?? []) as ColumnRow[],
      cards,
      assignees: (assignees.data ?? []) as AssigneeLink[],
      labels: (labels.data ?? []) as Label[],
      labelLinks: (labelLinks.data ?? []) as LabelLink[],
      memberIds: [...memberIds],
      people,
    };
  }

  /** The board row. If the open-to-staff column is not applied yet, the board is closed. */
  private async loadBoard(boardId: string): Promise<BoardRow> {
    const first = await supabase.from("hq_task_boards").select(BOARD_COLS).eq("id", boardId).maybeSingle();
    if (!first.error) {
      if (!first.data) throw new Error("Board not found, or you are not a member of it.");
      return { open_to_all_staff: false, ...(first.data as BoardRow) };
    }
    if (!missingOpenColumn(first.error)) fail(first.error, "open this board");
    const second = await supabase.from("hq_task_boards").select(BOARD_COLS_BEFORE_OPEN).eq("id", boardId).maybeSingle();
    if (second.error) fail(second.error, "open this board");
    if (!second.data) throw new Error("Board not found, or you are not a member of it.");
    return { open_to_all_staff: false, ...(second.data as BoardRow) };
  }

  /**
   * Cards include archived ones, so ?card=SS-12 and the archive view can
   * open them. The columns hide them until asked. If the archive columns
   * are not in the database yet, load the board without them.
   */
  private async loadCards(boardId: string): Promise<Card[]> {
    const first = await supabase.from("hq_tasks").select(CARD_COLS).eq("board_id", boardId).order("position");
    if (!first.error) return ((first.data ?? []) as Card[]).map(withArchive);
    if (!missingArchiveColumn(first.error)) fail(first.error, "load the cards");
    const second = await supabase.from("hq_tasks").select(CARD_COLS_BEFORE_ARCHIVE).eq("board_id", boardId).order("position");
    if (second.error) fail(second.error, "load the cards");
    return ((second.data ?? []) as Card[]).map(withArchive);
  }

  async listObjectives(): Promise<LinkableObjective[]> {
    const { data, error } = await supabase.from("hq_pe_objectives")
      .select("id,title,priority,deadline,status").eq("status", "active");
    if (error) fail(error, "load the company priorities");
    return linkableObjectives((data ?? []) as LinkableObjective[]);
  }

  async createCard(input: { boardId: string; columnId: string; title: string; position: number; createdBy: string }): Promise<Card> {
    const row = {
      board_id: input.boardId, column_id: input.columnId, title: input.title,
      position: input.position, created_by: input.createdBy,
    };
    let res = await supabase.from("hq_tasks").insert(row).select(CARD_COLS).single();
    // A select list that names a column the migration has not added fails the
    // whole request, insert included, so retrying does not create a second card.
    if (res.error && missingArchiveColumn(res.error)) {
      res = await supabase.from("hq_tasks").insert(row).select(CARD_COLS_BEFORE_ARCHIVE).single();
    }
    if (res.error || !res.data) fail(res.error, "add that card");
    return withArchive(res.data as Card);
  }

  async moveCard(cardId: string, columnId: string, position: number, opts?: { clearArchive?: boolean }): Promise<void> {
    const patch: Record<string, unknown> = { column_id: columnId, position };
    if (opts?.clearArchive) {
      patch.archived_at = null;
      patch.archived_by = null;
      patch.archive_reason = null;
    }
    const { error } = await supabase.from("hq_tasks").update(patch).eq("id", cardId);
    if (error) fail(error, "move that card");
  }

  async addAssignees(cardId: string, employeeIds: string[]): Promise<void> {
    if (employeeIds.length === 0) return;
    const { error } = await supabase.from("hq_task_assignees")
      .upsert(employeeIds.map(employee_id => ({ task_id: cardId, employee_id })), { onConflict: "task_id,employee_id", ignoreDuplicates: true });
    if (error) fail(error, "assign that card");
  }

  async createLabel(boardId: string, name: string, color: LabelColor): Promise<Label> {
    const { data, error } = await supabase.from("hq_task_labels")
      .insert({ board_id: boardId, name: name.trim(), color }).select(LABEL_COLS).single();
    if (error || !data) fail(error, "add that label");
    return data as Label;
  }

  async setCardLabel(cardId: string, labelId: string, on: boolean): Promise<void> {
    const { error } = on
      ? await supabase.from("hq_task_label_links").insert({ task_id: cardId, label_id: labelId })
      : await supabase.from("hq_task_label_links").delete().eq("task_id", cardId).eq("label_id", labelId);
    if (error) fail(error, "change that label");
  }

  async addMember(boardId: string, employeeId: string): Promise<void> {
    const { error } = await supabase.from("hq_board_members").insert({ board_id: boardId, employee_id: employeeId });
    if (error) fail(error, "add them to this board");
  }

  async removeMember(boardId: string, employeeId: string): Promise<number> {
    const { data, error } = await supabase.rpc("hq_board_remove_member", { p_board_id: boardId, p_employee_id: employeeId });
    if (error) fail(error, "remove them from this board");
    return Number(data ?? 0);
  }

  subscribe(boardId: string, onChange: (c: BoardChange) => void, onReconnect: () => void): () => void {
    let joinedOnce = false;
    const byBoard = `board_id=eq.${boardId}`;
    const ch = supabase.channel(`board-${boardId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "hq_tasks", filter: byBoard }, p => {
        if (p.eventType === "DELETE") onChange({ table: "hq_tasks", type: "DELETE", id: (p.old as { id: string }).id });
        else onChange({ table: "hq_tasks", type: p.eventType, row: p.new as Card });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "hq_task_columns", filter: byBoard }, p => {
        if (p.eventType === "DELETE") onChange({ table: "hq_task_columns", type: "DELETE", id: (p.old as { id: string }).id });
        else onChange({ table: "hq_task_columns", type: p.eventType, row: p.new as ColumnRow });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "hq_task_labels", filter: byBoard }, p => {
        if (p.eventType === "DELETE") onChange({ table: "hq_task_labels", type: "DELETE", id: (p.old as { id: string }).id });
        else onChange({ table: "hq_task_labels", type: p.eventType, row: p.new as Label });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "hq_board_members", filter: byBoard }, p => {
        if (p.eventType === "UPDATE") return;
        // Realtime cannot filter DELETEs, so check the board here. The primary
        // key is (board_id, employee_id), so both arrive on a delete.
        const row = (p.eventType === "DELETE" ? p.old : p.new) as { board_id?: string; employee_id?: string };
        if (row.board_id !== boardId || !row.employee_id) return;
        onChange({ table: "hq_board_members", type: p.eventType, employee_id: row.employee_id });
      })
      // These two have no board_id. RLS limits them to boards the viewer can
      // open, and applyChange drops any that are not on this board.
      .on("postgres_changes", { event: "*", schema: "public", table: "hq_task_assignees" }, p => {
        if (p.eventType === "UPDATE") return;
        const row = (p.eventType === "DELETE" ? p.old : p.new) as AssigneeLink;
        if (row.task_id && row.employee_id) onChange({ table: "hq_task_assignees", type: p.eventType, row });
        else onReconnect();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "hq_task_label_links" }, p => {
        if (p.eventType === "UPDATE") return;
        const row = (p.eventType === "DELETE" ? p.old : p.new) as LabelLink;
        if (row.task_id && row.label_id) onChange({ table: "hq_task_label_links", type: p.eventType, row });
        else onReconnect();
      })
      .subscribe(status => {
        if (status === "SUBSCRIBED") {
          if (joinedOnce) onReconnect();
          joinedOnce = true;
        }
      });
    return () => { void supabase.removeChannel(ch); };
  }
}
